import { PlayerCore } from '../core/player.js';
import { settings } from '../core/storage.js';
import { t } from '../core/i18n.js';
import { device } from '../core/device.js';
import { localServerBase } from '../core/transcode.js';
import { previewMuted, setPreviewMuted, onPreviewMute } from '../core/preview-sound.js';

/* Vista previa de escena sobre el fondo de una carátula (hero), al estilo Netflix:
   tras una pequeña espera arranca el vídeo silenciado, se funde sobre el backdrop,
   reproduce unos segundos y vuelve a la imagen. Un botón permite activar el sonido. */

const START_DELAY_MS = 1500;
const PREVIEW_SECONDS = 30;
// App de escritorio cargada desde disco (file://): el reproductor de YouTube dirige sus avisos («reproduciendo», «acabó») al
// origen https de la página, que aquí no existe, así que nunca llegan. En ese caso el tráiler se muestra en cuanto carga y se
// retira por tiempo; las órdenes hacia el iframe (silencio, pausa) sí funcionan.
const BLIND = typeof location !== 'undefined' && !/^https?:$/.test(location.protocol);
const BLIND_AUTO_MS = 150000; // vista previa muda automática: no más de dos minutos y medio

/** Dirección del reproductor de YouTube: sin controles, una sola pasada y con la API por mensajes. */
export function trailerEmbedUrl(id, start, muted) {
    const q = new URLSearchParams({ autoplay: '1', mute: muted ? '1' : '0', controls: '0', disablekb: '1', fs: '0', iv_load_policy: '3', modestbranding: '1', playsinline: '1', rel: '0', cc_load_policy: '0', enablejsapi: '1', start: String(Math.max(0, Math.floor(start || 0))) });
    // App de Windows (página cargada desde disco): YouTube no reproduce incrustado ahí; va a través de la página puente del
    // servidor local de la app, que tiene un origen web real y reenvía los mensajes en los dos sentidos.
    const relay = BLIND ? localServerBase() : '';
    if (relay) return relay + '/trailer?id=' + encodeURIComponent(id) + '&' + q.toString();
    try { q.set('origin', location.origin); } catch (e) { /* sin origin */ }
    return 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?' + q.toString();
}

/** ¿Llegan los avisos del reproductor (empieza, acaba, falla)? En la app de Windows sin página puente no. */
export const trailerEventsWork = () => !BLIND || !!localServerBase();

/** Id de YouTube del tráiler de una ficha si se puede incrustar en este aparato (navegador y app de Windows; no tele ni Android). */
export function embeddableTrailerId(info) {
    if (!info || !info.youtube || device.isTV || device.isCordova) return '';
    return String(info.youtube).replace(/.*[?&]v=|.*youtu\.be\//, '').split('&')[0];
}

export function previewEnabled() {
    if (settings.get('autoplayPreview') === false) return false;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    return true;
}

/* Solo puede haber una vista previa sonando a la vez en toda la app (una ficha que queda debajo de otra
   conserva su página, y con ella un tráiler que seguiría oyéndose). */
let current = null;

export class HeroPreview {
    /**
     * @param {() => HTMLElement|null} getHero  devuelve el .hero actual (puede repintarse)
     * @param {() => {url: string, startAt: number}|null} getSource  URL del stream y segundo de inicio
     * @param {() => string} [getTrailer]  id de YouTube del tráiler: si existe, se reproduce él en la cabecera
     *   (en vez de una escena del propio título)
     */
    constructor(getHero, getSource, getTrailer) {
        this.getHero = getHero;
        this.getSource = getSource;
        this.getTrailer = getTrailer || (() => '');
        this.frame = null;
        this.trailerTime = 0;
        this.trailerSound = false;
        this.pauseButton = null;
        this.userPaused = false;
        this.onMessage = null;
        this.onError = null; // si YouTube no deja reproducir el tráiler incrustado (lo decide quien lo abre: p. ej. abrirlo fuera)
        this.resizer = null;
        this.video = null;
        this.core = null;
        this.button = null;
        this.timer = null;
        this.stopTimer = null;
        this.active = false;
        this.muted = true;
        this.ctlTimer = null;
        this._tapHero = null;
        this._offMute = onPreviewMute((m) => { if (this.active) this.setMuted(m); }); // un solo estado de sonido para todas las vistas previas
        // Los botones de pausa y sonido no estorban: salen al tocar la cabecera mientras suena y se esconden solos.
        this._tap = (e) => {
            if (e.target && e.target.closest && e.target.closest('button, a, .btn, input, select')) return;
            const hero = this.getHero();
            if (hero && hero.classList && hero.classList.contains('is-cinema') && !(e.target && e.target.closest && e.target.closest('.hero-content'))) hero.classList.toggle('cinema-ui');
            this.reveal();
        };
    }

    _isPlayingUi() {
        const el = this.frame || this.video;
        return !!(el && el.classList.contains('is-playing'));
    }

    _setControls(show) {
        [this.button, this.pauseButton].forEach((b) => { if (b) b.hidden = !show; });
    }

    /** Enseña pausa y sonido unos segundos (en la tele con mando, siempre; en pausa se quedan para poder reanudar). */
    reveal() {
        if (!this._isPlayingUi()) return;
        this._setControls(true);
        clearTimeout(this.ctlTimer);
        if (device.isTV || this.userPaused) return;
        this.ctlTimer = setTimeout(() => this._setControls(false), 4000);
    }

    /**
     * Modo cine en la ficha (móvil y tablet): mientras suena el tráiler, el resto de la cabecera (título, botones, sinopsis, reparto) se
     * esconde para ver el vídeo limpio; un toque en el vídeo lo trae de vuelta y otro lo vuelve a esconder.
     */
    _cinema(on) {
        const hero = this.getHero();
        if (!hero || !hero.classList || !hero.classList.contains('detail-hero')) return;
        if (on && !document.documentElement.classList.contains('mode-touch')) return;
        hero.classList.toggle('is-cinema', !!on);
        if (!on) hero.classList.remove('cinema-ui');
    }

    /** Cuando el vídeo empieza a sonar: sin tocar nada no se enseña ningún botón (salvo en la tele, que se maneja con el mando). */
    _onPlayingUi() {
        this._cinema(true);
        if (device.isTV) this._setControls(true);
    }

    _bindTap() {
        const hero = this.getHero();
        if (hero === this._tapHero) return;
        if (this._tapHero) this._tapHero.removeEventListener('click', this._tap);
        this._tapHero = hero || null;
        if (hero) hero.addEventListener('click', this._tap);
    }

    schedule() {
        this.cancel();
        if (!previewEnabled()) return;
        this.timer = setTimeout(() => this.start(), START_DELAY_MS);
    }

    /** Reproduce el tráiler en la cabecera. `sound`: con sonido (lo pide el usuario) o mudo (arranque automático). */
    playTrailer({ sound = false } = {}) {
        const id = this.getTrailer();
        const hero = this.getHero();
        if (!id || !hero) return false;
        if (this.active && this.frame) {
            this.setMuted(!sound);
            return true;
        }
        this.stop();
        if (current && current !== this) current.stop();
        current = this;
        this.startTrailer(id, hero, sound);
        return true;
    }

    _trailerSrc(id, start, muted) {
        return trailerEmbedUrl(id, start, muted);
    }

    _ytCommand(func, args = []) {
        try { if (this.frame && this.frame.contentWindow) this.frame.contentWindow.postMessage(JSON.stringify({ event: 'command', func, args }), '*'); } catch (e) { /* aún no cargado */ }
    }

    setMuted(muted, { remember = false } = {}) {
        this.muted = muted;
        if (remember) setPreviewMuted(muted);
        if (this.video) this.video.muted = muted;
        this._ytCommand(muted ? 'mute' : 'unMute');
        if (!muted) this._ytCommand('setVolume', [100]);
        if (this.button) this.button.querySelector('.mi').textContent = muted ? 'volume_off' : 'volume_up';
    }

    setPaused(paused) {
        this.userPaused = paused;
        this._ytCommand(paused ? 'pauseVideo' : 'playVideo');
        this._setPauseIcon(paused);
        this.reveal(); // en pausa se quedan a la vista; al reanudar vuelven a esconderse solos
    }

    _setPauseIcon(paused) {
        const b = this.pauseButton;
        if (!b) return;
        b.querySelector('.mi').textContent = paused ? 'play_arrow' : 'pause';
        b.setAttribute('aria-label', t(paused ? 'preview.play' : 'preview.pause'));
    }

    /** El iframe de YouTube cubre la cabecera como un fondo (cover): se calcula su tamaño a partir del de ésta. */
    _fitTrailer() {
        const frame = this.frame;
        const bg = frame && frame.parentNode;
        if (!bg) return;
        const w = bg.clientWidth;
        const h = bg.clientHeight;
        if (!w || !h) return;
        const k = Math.max(w / 16, h / 9) * 1.12; // un poco de más: se pierden las franjas y el rótulo de YouTube
        frame.style.width = Math.ceil(16 * k) + 'px';
        frame.style.height = Math.ceil(9 * k) + 'px';
        frame.style.left = Math.round((w - 16 * k) / 2) + 'px';
        frame.style.top = Math.round((h - 9 * k) / 2) + 'px';
    }

    startTrailer(id, hero, sound) {
        this.active = true;
        this.muted = !sound;
        this.trailerSound = sound;
        this.trailerTime = 0;

        const frame = document.createElement('iframe');
        frame.className = 'hero-video hero-trailer';
        frame.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
        frame.setAttribute('tabindex', '-1');
        frame.setAttribute('aria-hidden', 'true');
        frame.title = 'Trailer';
        frame.src = this._trailerSrc(id, 0, this.muted);
        this.frame = frame;

        const button = document.createElement('button');
        button.className = 'hero-mute';
        button.type = 'button';
        button.hidden = true;
        button.setAttribute('aria-label', t('preview.sound'));
        button.innerHTML = '<span class="mi">' + (this.muted ? 'volume_off' : 'volume_up') + '</span>';
        button.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.setMuted(!this.muted, { remember: true });
        });
        this.button = button;

        // Pausa / reanudar. Solo aparece cuando el tráiler ya está sonando.
        const pauseButton = document.createElement('button');
        pauseButton.className = 'hero-mute hero-pause';
        pauseButton.type = 'button';
        pauseButton.hidden = true;
        pauseButton.setAttribute('aria-label', t('preview.pause'));
        pauseButton.innerHTML = '<span class="mi">pause</span>';
        pauseButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.setPaused(!this.userPaused);
        });
        this.pauseButton = pauseButton;
        this.userPaused = false;

        // Mensajes del reproductor: cuándo empieza (para fundirlo), en qué segundo va y cuándo acaba o falla.
        this.onMessage = (e) => {
            if (!this.frame || e.source !== this.frame.contentWindow) return;
            let data = e.data;
            if (typeof data === 'string') { try { data = JSON.parse(data); } catch (err) { return; } }
            if (!data || typeof data !== 'object') return;
            const info = data.info;
            if (data.event === 'onError') { this.stop(); if (this.onError) this.onError(data.info); return; }
            const state = data.event === 'onStateChange' ? info : info && typeof info === 'object' ? info.playerState : undefined;
            if (info && typeof info === 'object' && typeof info.currentTime === 'number') this.trailerTime = info.currentTime;
            if (state === 1) {
                clearTimeout(this.stopTimer);
                frame.classList.add('is-playing');
                this._onPlayingUi();
                this._ytCommand('unloadModule', ['captions']);
                // Si se recargó (la ficha se repintó) estando en pausa, se vuelve a pausar.
                if (this.userPaused) this._ytCommand('pauseVideo');
                else this._setPauseIcon(false);
            } else if (state === 2) {
                this._setPauseIcon(true);
            } else if (state === 0) {
                this.stop(); // acabó el tráiler: vuelve la imagen
            }
        };
        window.addEventListener('message', this.onMessage);
        frame.addEventListener('load', () => {
            // Sin este saludo, YouTube no manda sus eventos.
            try { frame.contentWindow.postMessage(JSON.stringify({ event: 'listening', id: 1, channel: 'widget' }), '*'); } catch (err) { /* nada */ }
            this._ytCommand('addEventListener', ['onStateChange']);
            this._ytCommand('unloadModule', ['captions']); // sin subtítulos automáticos sobre la cabecera
            if (BLIND && !localServerBase()) {
                // Sin página puente no llegan los avisos del reproductor: se da por reproduciendo poco después de cargar.
                clearTimeout(this.stopTimer);
                setTimeout(() => {
                    if (this.frame !== frame) return;
                    frame.classList.add('is-playing');
                    this._onPlayingUi();
                    this._setPauseIcon(false);
                }, 900);
                if (!sound) this.stopTimer = setTimeout(() => this.stop(), BLIND_AUTO_MS);
            }
        });
        // Si en unos segundos no ha empezado (vídeo no disponible, bloqueado por el navegador…), se deja la imagen.
        this.stopTimer = setTimeout(() => this.stop(), 10000);

        this.attach();
        if (typeof ResizeObserver !== 'undefined' && frame.parentNode) {
            this.resizer = new ResizeObserver(() => this._fitTrailer());
            this.resizer.observe(frame.parentNode);
        }
    }

    start() {
        if (this.getTrailer() && this.getHero()) {
            this.playTrailer({ sound: !previewMuted() });
            return;
        }
        const src = this.getSource();
        const hero = this.getHero();
        if (!src || !src.url || !hero) return;
        if (current && current !== this) current.stop();
        current = this;
        this.active = true;
        this.muted = previewMuted();

        const video = document.createElement('video');
        video.className = 'hero-video';
        video.muted = this.muted;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.preload = 'auto';
        // Android WebView pinta un botón de "play" gigante sobre los vídeos sin poster.
        video.poster = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
        this.video = video;

        const button = document.createElement('button');
        button.className = 'hero-mute';
        button.type = 'button';
        button.hidden = true;
        button.setAttribute('aria-label', t('preview.sound'));
        button.innerHTML = '<span class="mi">' + (this.muted ? 'volume_off' : 'volume_up') + '</span>';
        button.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.setMuted(!this.muted, { remember: true });
        });
        this.button = button;

        this.attach();

        const core = new PlayerCore(video);
        this.core = core;
        core.on('playing', () => {
            video.classList.add('is-playing');
            this._onPlayingUi();
            clearTimeout(this.stopTimer);
            this.stopTimer = setTimeout(() => this.stop(), PREVIEW_SECONDS * 1000);
        });
        core.on('ended', () => this.stop());
        core.on('error', () => this.stop());
        core.on('autoplayblocked', () => { this.muted = true; video.muted = true; if (this.button) this.button.querySelector('.mi').textContent = 'volume_off'; video.play().catch(() => this.stop()); }); // el navegador no deja arrancar con sonido: en silencio
        core.load({ url: src.url, startPosition: src.startAt || 0, muted: this.muted, autoplay: true }).catch(() => this.stop());
    }

    /** Vuelve a colocar el vídeo si la carátula se ha repintado. */
    attach() {
        if (this.active && this.frame) return this._attachTrailer();
        if (!this.active || !this.video) return;
        const hero = this.getHero();
        if (!hero) return;
        const bg = hero.querySelector('.hero-bg');
        if (bg && this.video.parentNode !== bg) bg.appendChild(this.video);
        if (this.button && this.button.parentNode !== hero) hero.appendChild(this.button);
        this._bindTap();
    }

    /** Coloca el tráiler en la cabecera. Mover un iframe lo recarga: se reanuda por el segundo en el que iba. */
    _attachTrailer() {
        const hero = this.getHero();
        const frame = this.frame;
        if (!hero || !frame) return;
        const bg = hero.querySelector('.hero-bg');
        if (bg && frame.parentNode !== bg) {
            if (frame.parentNode) frame.src = this._trailerSrc(this._trailerId(), this.trailerTime, this.muted);
            else frame.src = frame.src || '';
            bg.appendChild(frame);
            if (this.resizer) { this.resizer.disconnect(); this.resizer.observe(bg); }
        }
        if (this.button && this.button.parentNode !== hero) hero.appendChild(this.button);
        if (this.pauseButton && this.pauseButton.parentNode !== hero) hero.appendChild(this.pauseButton);
        this._bindTap();
        this._fitTrailer();
    }

    _trailerId() {
        try { return this.getTrailer() || ''; } catch (e) { return ''; }
    }

    cancel() {
        clearTimeout(this.timer);
        this.timer = null;
    }

    stop() {
        this.cancel();
        clearTimeout(this.stopTimer);
        this.stopTimer = null;
        if (current === this) current = null;
        clearTimeout(this.ctlTimer);
        if (this._tapHero) { this._tapHero.removeEventListener('click', this._tap); this._tapHero = null; }
        if (!this.active) return;
        this.active = false;
        this._cinema(false);
        if (this.frame) {
            const frame = this.frame;
            const button = this.button;
            if (this.onMessage) window.removeEventListener('message', this.onMessage);
            if (this.resizer) this.resizer.disconnect();
            frame.classList.remove('is-playing');
            if (button) button.remove();
            if (this.pauseButton) this.pauseButton.remove();
            this.pauseButton = null;
            this.userPaused = false;
            setTimeout(() => { frame.src = 'about:blank'; frame.remove(); }, 400);
            this.frame = null;
            this.button = null;
            this.onMessage = null;
            this.resizer = null;
            return;
        }
        const video = this.video;
        const button = this.button;
        if (video) video.classList.remove('is-playing');
        if (button) button.remove();
        if (this.core) {
            const core = this.core;
            // Dejar que termine el fundido antes de destruir el elemento.
            setTimeout(() => {
                core.destroy();
                if (video) video.remove();
            }, 400);
        }
        this.core = null;
        this.video = null;
        this.button = null;
    }

    destroy() {
        this.stop();
        if (this._offMute) { this._offMute(); this._offMute = null; }
    }
}
